/**
 * AlphaSearch purple palette and mode-specific primary/banner tokens.
 * Light mode primary stays on the brand step; dark mode uses one step lighter
 * from the same hue family for contrast on dark surfaces.
 *
 * Light banners keep the solid wash fill. Dark banners use today's muted
 * charcoal surface with a low-opacity primary glow — not the old #2A1F5C band.
 * Interactive primary (`DARK_MODE_PRIMARY`) stays independent of those surfaces.
 */

export const ALPHA_PURPLE = {
  /** Brand primary — light mode interactive purple */
  brand: "#4A2CF5",
  /** Mid-lift — dark mode pressed / secondary accent */
  light: "#775EFF",
  /** Previous dark-mode interactive step — hover on dark surfaces */
  bright: "#9580FF",
  /** Current dark-mode interactive primary — one step brighter than `bright` */
  brightLight: "#A894FF",
  /** Soft highlight on dark surfaces — badges, primary.light */
  accentHighlight: "#B8A8FF",
  /** One step darker — hover / emphasis on light surfaces */
  dark: "#3B23C6",
  tertiary: "#7141ff",
  /** Dark-mode surface tint (legacy secondary) */
  surfaceTint: "#2A1F5C",
  /** Light-mode wash (legacy secondary) */
  wash: "#F6F4FF",
};

export const LIGHT_MODE_PRIMARY = ALPHA_PURPLE.brand;
/** Interactive purple on dark surfaces — brighter than prior dark primary. */
export const DARK_MODE_PRIMARY = ALPHA_PURPLE.brightLight;
export const DARK_MODE_PRIMARY_HOVER = ALPHA_PURPLE.bright;
export const DARK_MODE_PRIMARY_MUTED = ALPHA_PURPLE.light;

/** RGB tuple for rgba() accents derived from `DARK_MODE_PRIMARY`. */
export const DARK_MODE_PRIMARY_RGB = "168, 148, 255";

export const CSS_PRIMARY_VAR = "--primary";
export const CSS_PRIMARY_DARK_MODE_VAR = "--primary-dark-mode";
export const CSS_PRIMARY_LIGHT_MODE_VAR = "--primary-light-mode";

/** Dark page canvas — banner dissolves into this */
export const DARK_PAGE_BACKGROUND = "#121212";
/** Dark banner top surface — charcoal, not royal purple */
export const DARK_BANNER_SURFACE = "#181522";
export const DARK_BANNER_MID = "#151319";
export const DARK_BANNER_DEEP = "#131218";

export function getPrimaryForMode(isDark) {
  return isDark ? DARK_MODE_PRIMARY : LIGHT_MODE_PRIMARY;
}

/** Theme-aware rgba accent using the mode-appropriate primary hue. */
export function primaryAccentAlpha(isDark, opacity) {
  return isDark
    ? `rgba(${DARK_MODE_PRIMARY_RGB}, ${opacity})`
    : `rgba(74, 44, 245, ${opacity})`;
}

/**
 * Dark Search-style banner: charcoal glow through the header that eases into
 * `DARK_BANNER_DEEP` at the bottom so the filter band can finish the fade.
 */
export function getDarkModeBannerGradient(backgroundDefault = DARK_PAGE_BACKGROUND) {
  return [
    `radial-gradient(ellipse 110% 200% at 38% 0%, rgba(${DARK_MODE_PRIMARY_RGB}, 0.16) 0%, rgba(149, 128, 255, 0.09) 32%, rgba(119, 94, 255, 0.04) 58%, rgba(${DARK_MODE_PRIMARY_RGB}, 0.012) 78%, transparent 100%)`,
    `linear-gradient(180deg, ${DARK_BANNER_SURFACE} 0%, #16141c 16%, ${DARK_BANNER_MID} 34%, #14121a 58%, ${DARK_BANNER_DEEP} 82%, ${DARK_BANNER_DEEP} 100%)`,
  ].join(", ");
}

/** Light Search-style banner — solid wash fill. */
export function getLightModeBannerGradient() {
  return ALPHA_PURPLE.wash;
}

/** Soft wash behind cards that sit under the banner zone (Saved Search enrich). */
export function getDarkModeBannerGradientSoft() {
  return [
    `radial-gradient(ellipse 80% 100% at 30% 0%, rgba(${DARK_MODE_PRIMARY_RGB}, 0.11) 0%, transparent 65%)`,
    `linear-gradient(180deg, ${DARK_BANNER_MID} 0%, ${DARK_BANNER_DEEP} 45%, transparent 100%)`,
  ].join(", ");
}

export function getLightModeBannerGradientSoft() {
  const { wash } = ALPHA_PURPLE;
  return `linear-gradient(180deg, ${wash} 0%, ${wash} 55%, transparent 100%)`;
}

export function getDarkModeBannerGradientExtended(backgroundDefault = DARK_PAGE_BACKGROUND) {
  return [
    `radial-gradient(ellipse 95% 120% at 32% -10%, rgba(${DARK_MODE_PRIMARY_RGB}, 0.14) 0%, rgba(149, 128, 255, 0.08) 38%, transparent 70%)`,
    `linear-gradient(180deg, ${DARK_BANNER_SURFACE} 0px, ${DARK_BANNER_MID} 80px, ${DARK_BANNER_DEEP} 160px, ${backgroundDefault} 220px, ${backgroundDefault} 100%)`,
  ].join(", ");
}

export function getLightModeBannerGradientExtended(backgroundDefault = "#ffffff") {
  const { wash } = ALPHA_PURPLE;
  return `linear-gradient(180deg, ${wash} 0px, ${wash} 120px, ${backgroundDefault} 220px, ${backgroundDefault} 100%)`;
}

/** Continues the banner's lower ease into the page canvas above the toolbar. */
export function getDarkModeFilterBandGradient(backgroundDefault = DARK_PAGE_BACKGROUND) {
  return [
    `radial-gradient(ellipse 110% 90px at 38% 0%, rgba(${DARK_MODE_PRIMARY_RGB}, 0.04) 0%, transparent 72%)`,
    `linear-gradient(180deg, ${DARK_BANNER_DEEP} 0%, ${backgroundDefault} 55%, ${backgroundDefault} 100%)`,
  ].join(", ");
}

export function getLightModeFilterBandGradient(backgroundDefault = "#ffffff") {
  const { wash } = ALPHA_PURPLE;
  return `linear-gradient(180deg, ${wash} 0%, ${wash} 60%, ${backgroundDefault} 100%)`;
}

export function getDarkModeEnrichPanelGradient() {
  return [
    `radial-gradient(ellipse 70% 80% at 8% 20%, rgba(${DARK_MODE_PRIMARY_RGB}, 0.12) 0%, transparent 55%)`,
    `linear-gradient(180deg, #16141f 0%, #141218 40%, ${DARK_PAGE_BACKGROUND} 100%)`,
  ].join(", ");
}

export function buildBannerPalette(isDark, backgroundDefault) {
  const pageBg = backgroundDefault ?? (isDark ? DARK_PAGE_BACKGROUND : "#ffffff");
  return {
    gradient: isDark ? getDarkModeBannerGradient(pageBg) : getLightModeBannerGradient(),
    gradientSoft: isDark
      ? getDarkModeBannerGradientSoft()
      : getLightModeBannerGradientSoft(),
    gradientExtended: isDark
      ? getDarkModeBannerGradientExtended(pageBg)
      : getLightModeBannerGradientExtended(pageBg),
    filterBand: isDark
      ? getDarkModeFilterBandGradient(pageBg)
      : getLightModeFilterBandGradient(pageBg),
    flat: isDark ? DARK_BANNER_SURFACE : ALPHA_PURPLE.wash,
  };
}

export function applyThemeCssVariables(isDark) {
  const root = document.documentElement;
  const activePrimary = getPrimaryForMode(isDark);
  root.style.setProperty(CSS_PRIMARY_VAR, activePrimary);
  root.style.setProperty(CSS_PRIMARY_DARK_MODE_VAR, DARK_MODE_PRIMARY);
  root.style.setProperty(CSS_PRIMARY_LIGHT_MODE_VAR, LIGHT_MODE_PRIMARY);
}
